import {
  fork,
  put,
  all,
  call,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);
const functions = getFunctions(rsf.app);

const normalizeDesign = (design) => {
  if (!design) return design;
  if (typeof design !== 'string') return design;

  try {
    return JSON.parse(design);
  } catch (error) {
    return design;
  }
};

const demTemplateTransformer = (payload) => {
  const demTemplates = [];
  payload.forEach((doc) => {
    const data = doc.data();

    demTemplates.push({
      id: doc.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(doc, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(doc, data, 'updatedAt').toDate(),
      }),
    });
  });
  return demTemplates;
};

function* syncDemTemplatesSaga() {
  const companyId = yield select((state) => state.Dashboard.companyId);

  const demTemplatesRef = query(
    collection(db, 'demTemplates'),
    where('companyId', '==', companyId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, demTemplatesRef, {
    successActionCreator: actions.syncDemTemplatesSuccess,
    failureActionCreator: actions.syncDemTemplatesFailure,
    transform: (payload) => demTemplateTransformer(payload),
  });

  yield take(types.RESET_DEM_TEMPLATE_STATE);
  yield cancel(task);
}

function* fetchDemTemplateSaga({ templateId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const docRef = doc(db, 'demTemplates', templateId);
    const snap = yield call(rsf.firestore.getDocument, docRef);

    if (!snap.exists) throw new Error('Template not found');

    const data = snap.data();
    if (data.companyId !== companyId) throw new Error('Template not found');
    const template = {
      id: snap.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(snap, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(snap, data, 'updatedAt').toDate(),
      }),
    };

    yield put(actions.fetchDemTemplateSuccess(template));
  } catch (error) {
    yield put(actions.fetchDemTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* createDemTemplateSaga({ template }) {
  try {
    const demTemplatesRef = collection(db, 'demTemplates');
    const companyId = yield select((state) => state.Dashboard.companyId);

    const { design, ...templateData } = template;

    yield call(rsf.firestore.addDocument, demTemplatesRef, {
      ...templateData,
      design: JSON.stringify(design),
      companyId,
      archived: false,
      createdAt: serverTimestamp(),
    });
    yield put(actions.createDemTemplateSuccess());
    toastr.success(i18n.t('Template created!'), '');
  } catch (error) {
    yield put(actions.createDemTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* updateDemTemplateSaga({ template }) {
  try {
    const { id, templateId, ...templateData } = template;
    const demTemplateId = templateId || id;
    if (!demTemplateId) throw new Error('Template not found');

    const companyId = yield select((state) => state.Dashboard.companyId);
    const docRef = doc(db, 'demTemplates', demTemplateId);

    const design = JSON.stringify(templateData.design);
    delete templateData.design;

    yield call(
      rsf.firestore.setDocument,
      docRef,
      {
        ...templateData,
        design,
        companyId,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    yield put(actions.updateDemTemplateSuccess());
    toastr.success(i18n.t('Template updated!'), '');
  } catch (error) {
    yield put(actions.updateDemTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* duplicateDemTemplateSaga({ templateId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const docRef = doc(db, 'demTemplates', templateId);
    const snap = yield call(rsf.firestore.getDocument, docRef);

    const exists =
      typeof snap.exists === 'function' ? snap.exists() : snap.exists;
    if (!exists) throw new Error('Template not found');

    const data = snap.data();
    if (data.companyId !== companyId) throw new Error('Template not found');

    const { createdAt, updatedAt, archived, ...templateData } = data;
    yield call(rsf.firestore.addDocument, collection(db, 'demTemplates'), {
      ...templateData,
      companyId,
      name: `${data.name || 'Template'} (copia)`,
      archived: false,
      createdAt: serverTimestamp(),
    });

    yield put(actions.duplicateDemTemplateSuccess());
    toastr.success(i18n.t('Template duplicated!'), '');
  } catch (error) {
    yield put(actions.duplicateDemTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* archiveDemTemplateSaga({ templateId }) {
  try {
    const docRef = doc(db, 'demTemplates', templateId);

    yield call(
      rsf.firestore.setDocument,
      docRef,
      { archived: true },
      { merge: true },
    );

    yield put(actions.archiveDemTemplateSuccess());
    toastr.success(i18n.t('Template archived!'), '');
  } catch (error) {
    yield put(actions.archiveDemTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* generateAiDemTemplateSaga({ prompt, currentDesign }) {
  try {
    const allMessages = yield select((state) => state.DemTemplate.ai.messages);
    const history = allMessages
      .slice(0, -1)
      .map(({ role, content }) => ({ role, content }));

    const generateFn = httpsCallable(functions, 'generateAiDem-generateAiDem');
    const { data } = yield call(generateFn, { prompt, currentDesign, history });

    if (data.error) throw new Error(data.error.message);

    yield put(
      actions.generateAiDemTemplateSuccess({
        message: data.data.message,
        design: data.data.design,
        designSnapshotBefore: currentDesign || null,
      }),
    );
  } catch (error) {
    yield put(actions.generateAiDemTemplateFailure(error.message));
    toastr.error(i18n.t('Error during AI generation'), '');
  }
}

function* demTemplateSaga() {
  yield all([
    takeLatest(types.SYNC_DEM_TEMPLATES.REQUEST, syncDemTemplatesSaga),
    takeLeading(types.FETCH_DEM_TEMPLATE.REQUEST, fetchDemTemplateSaga),
    takeLeading(types.CREATE_DEM_TEMPLATE.REQUEST, createDemTemplateSaga),
    takeLeading(types.UPDATE_DEM_TEMPLATE.REQUEST, updateDemTemplateSaga),
    takeLeading(types.DUPLICATE_DEM_TEMPLATE.REQUEST, duplicateDemTemplateSaga),
    takeLeading(types.ARCHIVE_DEM_TEMPLATE.REQUEST, archiveDemTemplateSaga),
    takeLeading(
      types.GENERATE_AI_DEM_TEMPLATE.REQUEST,
      generateAiDemTemplateSaga,
    ),
  ]);
}

export default demTemplateSaga;
